// structuralWarnings.ts — TCA / ACI 551.2R structural validation checks
// All values in INCHES (matches panelvar coordinate system).
// Pure function — no store access, no side effects.
// Reference: TCA Design Guide, ACI 551.2R, Dayton Superior tilt-up guidelines.

import type { PanelData, ProjectSettings } from '../types/panelvar';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type WarnSeverity = 'error' | 'warn';
export type WarnCategory = 'geometry' | 'opening' | 'lifting' | 'bracing' | 'reinforcement';

export interface StructuralWarning {
  id:         string;        // Unique check key e.g. 'geom-thickness-min'
  severity:   WarnSeverity;
  category:   WarnCategory;
  message:    string;        // Human-readable, includes measured value
  entityKey?: string;        // e.g. 'rovar-0' for per-entity warnings
}

// Normalized bounding box for an opening (all in inches from panel origin)
interface OpeningBox {
  key:    string;   // e.g. 'rovar-0'
  mark:   string;
  left:   number;
  right:  number;
  bottom: number;
  top:    number;
  area:   number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function fmt(inches: number): string {
  const ft  = Math.floor(Math.abs(inches) / 12);
  const rem = Math.abs(inches) % 12;
  const sign = inches < 0 ? '-' : '';
  if (ft === 0) return `${sign}${rem}"`;
  if (rem === 0) return `${sign}${ft}'-0"`;
  return `${sign}${ft}'-${rem}"`;
}

function fmtRatio(r: number): string {
  return (r * 100).toFixed(0) + '%';
}

// ─────────────────────────────────────────────────────────────────────────────
// Opening normalizer — produces bounding boxes for every enabled opening
// (rovar/wdvar/drvar/dlvar/sbvar/rbvar)
// ─────────────────────────────────────────────────────────────────────────────

function collectOpenings(panel: PanelData, ffeY: number): OpeningBox[] {
  const boxes: OpeningBox[] = [];

  // rovar — center-based
  panel.rovar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    const hw = d.width / 2, hh = d.height / 2;
    boxes.push({
      key: `rovar-${i}`, mark: d.mark || `RO-${i + 1}`,
      left: d.x - hw, right: d.x + hw,
      bottom: d.y + ffeY - hh, top: d.y + ffeY + hh,
      area: d.width * d.height,
    });
  });

  // wdvar — center-based
  panel.wdvar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    const hw = d.width / 2, hh = d.height / 2;
    boxes.push({
      key: `wdvar-${i}`, mark: d.mark || `WD-${i + 1}`,
      left: d.x - hw, right: d.x + hw,
      bottom: d.y + ffeY - hh, top: d.y + ffeY + hh,
      area: d.width * d.height,
    });
  });

  // drvar — y is BOTTOM from FFE; convert to panel-bottom-relative
  panel.drvar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    const hw = d.width / 2;
    boxes.push({
      key: `drvar-${i}`, mark: d.mark || `DR-${i + 1}`,
      left: d.x - hw, right: d.x + hw,
      bottom: d.y + ffeY, top: d.y + ffeY + d.height,
      area: d.width * d.height,
    });
  });

  // dlvar — center-based
  panel.dlvar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    const hw = d.width / 2, hh = d.height / 2;
    boxes.push({
      key: `dlvar-${i}`, mark: d.mark || `DL-${i + 1}`,
      left: d.x - hw, right: d.x + hw,
      bottom: d.y + ffeY - hh, top: d.y + ffeY + hh,
      area: d.width * d.height,
    });
  });

  // sbvar — center-based
  panel.sbvar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    const hw = d.width / 2, hh = d.height / 2;
    boxes.push({
      key: `sbvar-${i}`, mark: d.mark || `SB-${i + 1}`,
      left: d.x - hw, right: d.x + hw,
      bottom: d.y + ffeY - hh, top: d.y + ffeY + hh,
      area: d.width * d.height,
    });
  });

  // rbvar — circular, use bounding box
  panel.rbvar.forEach((s, i) => {
    if (!s.enabled) return;
    const d = s.data;
    const r = d.diameter / 2;
    boxes.push({
      key: `rbvar-${i}`, mark: d.mark || `RB-${i + 1}`,
      left: d.x - r, right: d.x + r,
      bottom: d.y + ffeY - r, top: d.y + ffeY + r,
      area: Math.PI * r * r,
    });
  });

  return boxes;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

export function getStructuralWarnings(
  panel: PanelData,
  settings: ProjectSettings,
): StructuralWarning[] {
  if (!settings.developerMode) return [];

  const warnings: StructuralWarning[] = [];
  const mp = panel.mpvar;
  const w  = mp.width;
  const h  = mp.height;
  const t  = mp.thickness;
  const ffeY = -(mp.botElev ?? 0) * 12;

  // ── Geometry ────────────────────────────────────────────────────────────────

  if (t < 5.5) {
    warnings.push({
      id: 'geom-thickness-min', severity: 'error', category: 'geometry',
      message: `Panel thickness (${fmt(t)}) is below the ACI 551.2R minimum of 5½".`,
    });
  } else if (t < 7.25) {
    warnings.push({
      id: 'geom-thickness-low', severity: 'warn', category: 'geometry',
      message: `Panel thickness (${fmt(t)}) is below the typical TCA default of 7¼". Verify loads.`,
    });
  }

  if (h > 0 && t > 0 && h / t > 50) {
    warnings.push({
      id: 'geom-slenderness', severity: 'warn', category: 'geometry',
      message: `Slenderness ratio h/t = ${(h / t).toFixed(1)} exceeds ACI 318 slender wall limit of 50. Check P-Δ deflections.`,
    });
  }

  if (h > 480) {
    warnings.push({
      id: 'geom-height-max', severity: 'warn', category: 'geometry',
      message: `Panel height (${fmt(h)}) exceeds 40 ft. Engineering review required for extraordinary panels.`,
    });
  }

  if (h > 0 && h <= 288 && w > 432) {
    warnings.push({
      id: 'geom-width-short-panel', severity: 'warn', category: 'geometry',
      message: `Panel width (${fmt(w)}) exceeds 36 ft for a panel ≤24 ft tall. Crane reach and rigging limits apply (Dayton Superior).`,
    });
  }

  if (h > 288 && w > 288) {
    warnings.push({
      id: 'geom-width-tall-panel', severity: 'warn', category: 'geometry',
      message: `Panel width (${fmt(w)}) exceeds 24 ft for a panel >24 ft tall. Lifting and casting constraints require review.`,
    });
  }

  // ── Openings ────────────────────────────────────────────────────────────────

  const openings = collectOpenings(panel, ffeY);

  openings.forEach((o) => {
    const leftLeg   = o.left;
    const rightLeg  = w > 0 ? w - o.right : Infinity;
    const topLeg    = h > 0 ? h - o.top   : Infinity;
    const bottomLeg = o.bottom;

    if (leftLeg < 24) {
      warnings.push({
        id: 'open-left-leg', severity: 'error', category: 'opening',
        entityKey: o.key,
        message: `${o.mark}: left concrete leg (${fmt(Math.round(leftLeg))}) is less than the TCA minimum 2'-0". Risk of panel splitting at lift.`,
      });
    }

    if (rightLeg < 24) {
      warnings.push({
        id: 'open-right-leg', severity: 'error', category: 'opening',
        entityKey: o.key,
        message: `${o.mark}: right concrete leg (${fmt(Math.round(rightLeg))}) is less than the TCA minimum 2'-0".`,
      });
    }

    if (bottomLeg < 0) {
      warnings.push({
        id: 'open-bottom-leg', severity: 'error', category: 'opening',
        entityKey: o.key,
        message: `${o.mark}: opening bottom (${fmt(Math.round(bottomLeg))}) extends below the panel base. Check position.`,
      });
    }

    if (topLeg < 24) {
      warnings.push({
        id: 'open-top-leg', severity: 'warn', category: 'opening',
        entityKey: o.key,
        message: `${o.mark}: top concrete leg (${fmt(Math.round(topLeg))}) is less than 2'-0". Consider parapet reinforcement.`,
      });
    }
  });

  // Cross-pair gap check — all opening types combined
  for (let a = 0; a < openings.length; a++) {
    for (let b = a + 1; b < openings.length; b++) {
      const A = openings[a], B = openings[b];
      // Minimum separation between bounding boxes in any axis
      const hGap = Math.max(A.left - B.right, B.left - A.right);
      const vGap = Math.max(A.bottom - B.top, B.bottom - A.top);
      const gap  = Math.max(hGap, vGap);  // positive = separated, negative = overlapping
      if (gap < 24) {
        warnings.push({
          id: 'open-gap', severity: 'error', category: 'opening',
          entityKey: A.key,
          message: `${A.mark} and ${B.mark}: gap between openings (${gap < 0 ? 'overlapping' : fmt(Math.round(gap))}) is less than the TCA minimum 2'-0".`,
        });
      }
    }
  }

  // Opening area ratio
  if (w > 0 && h > 0 && openings.length > 0) {
    const totalArea = openings.reduce((s, o) => s + o.area, 0);
    const ratio     = totalArea / (w * h);
    if (ratio > 0.40) {
      warnings.push({
        id: 'open-area-ratio', severity: 'warn', category: 'opening',
        message: `Total opening area (${fmtRatio(ratio)} of panel) exceeds 40%. Structural review required — consider pilasters or supplemental framing.`,
      });
    }
  }

  // ── Lifting (pick points) ────────────────────────────────────────────────────

  const enabledPicks = panel.ppvar.filter(s => s.enabled);
  if (enabledPicks.length === 0) {
    warnings.push({
      id: 'lift-no-picks', severity: 'warn', category: 'lifting',
      message: 'No pick points defined. Lifting insert layout is required before fabrication.',
    });
  } else {
    enabledPicks.forEach((s, i) => {
      const pp = s.data;
      const idx = panel.ppvar.findIndex((x, j) => x === s && j >= i);
      const key = `ppvar-${idx < 0 ? i : idx}`;

      if (w > 0 && pp.x < 15) {
        warnings.push({
          id: 'lift-pick-left', severity: 'warn', category: 'lifting',
          entityKey: key,
          message: `${pp.mark || key}: pick point is only ${fmt(pp.x)} from the left edge. Minimum 15" recommended (insert shear cone).`,
        });
      }
      if (w > 0 && w - pp.x < 15) {
        warnings.push({
          id: 'lift-pick-right', severity: 'warn', category: 'lifting',
          entityKey: key,
          message: `${pp.mark || key}: pick point is only ${fmt(Math.round(w - pp.x))} from the right edge. Minimum 15" recommended.`,
        });
      }
      if (h > 0 && pp.y < 15) {
        warnings.push({
          id: 'lift-pick-bottom', severity: 'warn', category: 'lifting',
          entityKey: key,
          message: `${pp.mark || key}: pick point is only ${fmt(pp.y)} from the bottom. Minimum 15" recommended.`,
        });
      }
      if (h > 0 && h - pp.y < 15) {
        warnings.push({
          id: 'lift-pick-top', severity: 'warn', category: 'lifting',
          entityKey: key,
          message: `${pp.mark || key}: pick point is only ${fmt(Math.round(h - pp.y))} from the top. Minimum 15" recommended.`,
        });
      }
    });
  }

  // ── Bracing ─────────────────────────────────────────────────────────────────

  const enabledBraces = panel.bpvar.filter(s => s.enabled).length;
  if (enabledBraces === 0) {
    warnings.push({
      id: 'brace-none', severity: 'warn', category: 'bracing',
      message: 'No brace points defined. Minimum 2 braces per panel required (TCA 18.1).',
    });
  } else if (enabledBraces === 1) {
    warnings.push({
      id: 'brace-min', severity: 'warn', category: 'bracing',
      message: 'Only 1 brace point defined. TCA 18.1 requires a minimum of 2 temporary braces per panel.',
    });
  }

  // ── Reinforcement (project settings) ────────────────────────────────────────

  if (settings.concreteStrength < 3000) {
    warnings.push({
      id: 'concrete-min', severity: 'error', category: 'reinforcement',
      message: `Concrete strength (${settings.concreteStrength} psi) is below the minimum 3,000 psi for tilt-up panels.`,
    });
  } else if (settings.concreteStrength < 4000) {
    warnings.push({
      id: 'concrete-low', severity: 'warn', category: 'reinforcement',
      message: `Concrete strength (${settings.concreteStrength} psi) is below the TCA typical default of 4,000 psi.`,
    });
  }

  if (settings.clearCover < 0.75) {
    warnings.push({
      id: 'rebar-cover-min', severity: 'error', category: 'reinforcement',
      message: `Clear cover (${settings.clearCover}") is below the ACI 318 minimum of ¾" for tilt-up panels.`,
    });
  }

  if (settings.rebarSpacing > 18) {
    warnings.push({
      id: 'rebar-spacing-max', severity: 'warn', category: 'reinforcement',
      message: `Rebar spacing (${settings.rebarSpacing}" o.c.) exceeds the TCA typical maximum of 18" for temperature/shrinkage steel.`,
    });
  }

  return warnings;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers consumed by UI components
// ─────────────────────────────────────────────────────────────────────────────

export function countBySeverity(warnings: StructuralWarning[]): { errors: number; warns: number } {
  return {
    errors: warnings.filter(w => w.severity === 'error').length,
    warns:  warnings.filter(w => w.severity === 'warn').length,
  };
}

export const CATEGORY_LABELS: Record<WarnCategory, string> = {
  geometry:      'Geometry',
  opening:       'Openings',
  lifting:       'Lifting',
  bracing:       'Bracing',
  reinforcement: 'Reinforcement',
};

export const CATEGORIES: WarnCategory[] = [
  'geometry', 'opening', 'lifting', 'bracing', 'reinforcement',
];
